import React, { useCallback, useState } from 'react';
import { differenceInSeconds, format } from 'date-fns';

import { useTranslation } from '@lyearn/i18n';
import { ArrowIosForward, InfoOutline, Trash2Outline } from '@lyearn/icons';
import {
  Avatar,
  Box,
  IconButton,
  Loader,
  Stack,
  Tooltip,
  Typography,
  useSnackbar,
} from '@lyearn/ui';

import { useUpdateTimeLogMutation } from '../../graphql/mutations/generated/updateTimeLog';
import { TimeLogFragment, useTimeLogsQuery } from '../../graphql/queries/generated/timeLogs';
import EntryEditModal from './EntryEditModal';

export const EntriesView = ({ projectId, taskId }: { projectId: string; taskId: string }) => {
  const { t } = useTranslation(['Task']);
  const [expandedUserIds, setExpandedUserIds] = useState<string[]>([]);
  const [editingTimeLog, setEditingTimeLog] = useState<TimeLogFragment | null>(null);
  const [{ data, fetching }] = useTimeLogsQuery({
    variables: {
      first: -1,
      filters: {
        entityIds: [taskId],
      },
    },
  });
  const { showSnackbar, showErrorSnackbar } = useSnackbar();

  const [{ fetching: updatingTimeLog }, updateTimeLog] = useUpdateTimeLogMutation();

  const timeLogs = data?.timeLogs?.edges?.map((edge) => edge?.node).filter(Boolean);

  const timeLogsByUser = timeLogs?.reduce(
    (acc, timeLog) => {
      acc[timeLog.userId] = [...(acc[timeLog.userId] || []), timeLog];
      return acc;
    },
    {} as Record<string, TimeLogFragment[]>,
  );

  const handleToggle = (userId: string) => {
    setExpandedUserIds((prev) =>
      prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId],
    );
  };

  const onDeleteTimeLog = useCallback(
    (timeLogId: string) => {
      updateTimeLog({
        data: {
          removeTimeLog: timeLogId,
        },
      }).then((res) => {
        if (res?.data?.updateTimeLog?.success) {
          showSnackbar({ message: 'Time Log Entry Deleted Successfully!' });
        } else {
          showErrorSnackbar({ message: t('Common:Error.SomethingWentWrong') });
        }
      });
    },
    [showErrorSnackbar, showSnackbar, t, updateTimeLog],
  );

  if (fetching) {
    return (
      <Stack
        className="mt-24 min-w-[385px] min-h-[200px] rounded-12 items-center justify-center"
        direction="col">
        <Loader className="mx-auto" size={40} />
      </Stack>
    );
  }

  return (
    <>
      <Stack
        className="mt-24 min-w-[385px] overflow-y-auto rounded-12 items-center justify-center"
        direction="col">
        <Stack className="border border-border rounded-12 p-16" direction="col">
          {timeLogsByUser && Object.keys(timeLogsByUser).length > 0 ? (
            Object.entries(timeLogsByUser).map(([userId, userTimeLogs], index) => {
              const isExpanded = expandedUserIds.includes(userId);
              const user = userTimeLogs[0]?.user;
              const totalSeconds = userTimeLogs.reduce((acc, timeLog) => {
                const startTime = new Date(timeLog.startTime);
                const endTime = new Date(timeLog.endTime ?? new Date().toISOString());
                return acc + differenceInSeconds(endTime, startTime);
              }, 0);

              const hours = Math.floor(totalSeconds / 3600);
              const minutes = Math.floor((totalSeconds % 3600) / 60);

              const totalTime = `${hours}h ${minutes}m`;

              return (
                <Box key={userId}>
                  <Stack direction="col">
                    <Stack
                      alignItems="center"
                      className="cursor-pointer"
                      direction="row"
                      justifyContent="between"
                      onClick={() => handleToggle(userId)}>
                      <Stack alignItems="center">
                        <ArrowIosForward
                          className={`mr-8 transition-transform duration-200 ${
                            isExpanded ? 'rotate-90' : ''
                          }`}
                          fillColorToken="icon-secondary"
                          height={20}
                          width={20}
                        />
                        <Avatar className="mr-4" size="x-small" src={user?.profilePicture || ''} />
                        <Typography variant="body-short-01">{user?.name}</Typography>
                      </Stack>
                      <Typography className="text-text-secondary" variant="body-short-01">
                        {totalTime}
                      </Typography>
                    </Stack>
                    {isExpanded &&
                      userTimeLogs.map((timeLog) => (
                        <Stack
                          key={timeLog.id}
                          className="cursor-pointer"
                          direction="col"
                          onClick={() => setEditingTimeLog(timeLog)}>
                          <Stack className="h-[1px] my-12 -mx-16 bg-border" />
                          <Stack direction="col">
                            <Stack
                              alignItems="center"
                              className="ml-12"
                              direction="row"
                              gapX={12}
                              justifyContent="between">
                              <Typography variant="body-short-02">
                                {`${format(
                                  new Date(timeLog.startTime),
                                  'EEE, MMM d, h:mm a',
                                )} - ${format(
                                  new Date(timeLog.endTime ?? new Date().toISOString()),
                                  'h:mm a',
                                )}`}
                              </Typography>
                              <Stack alignItems="center" direction="row" gapX={8}>
                                <Tooltip title={timeLog.instruction || 'No Notes Added'}>
                                  <InfoOutline height={16} width={16} />
                                </Tooltip>
                                <IconButton
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    onDeleteTimeLog(timeLog.id);
                                  }}>
                                  <Trash2Outline fillColorToken="icon-error" />
                                </IconButton>
                              </Stack>
                            </Stack>
                          </Stack>
                        </Stack>
                      ))}
                  </Stack>
                  {index !== Object.keys(timeLogsByUser).length - 1 ? (
                    <Stack className="h-[1px] my-12 -mx-16 bg-border" />
                  ) : null}
                </Box>
              );
            })
          ) : (
            <Stack alignItems="center" className="min-h-[250px]" justifyContent="center">
              <Typography className="text-text-secondary" variant="body-short-01">
                No Entries found
              </Typography>
            </Stack>
          )}
        </Stack>
      </Stack>
      {editingTimeLog ? (
        <EntryEditModal
          open={!!editingTimeLog}
          timeLog={editingTimeLog}
          onClose={() => setEditingTimeLog(null)}
        />
      ) : null}
    </>
  );
};
